import {
    AttioIgnoreRule,
    AttioIgnoreRuleType,
    CrmCompany,
    CrmContact,
    CrmFact,
    CrmProcessJob,
    CrmProcessJobSourceProgress,
    CrmRecallResult,
    CrmRunResult,
    CrmTeamMember,
    FACT_CATEGORY_COLORS,
    FACT_CATEGORY_LABELS,
    FileSearchByCompanyResult,
    FileSearchDocContent,
    FileSearchDocInfo,
    FileSearchQueryResult,
    FileSearchStoreInfo,
} from '@numeo/types'
import { oracleServiceAxios } from 'setup/SetupAxios'

export { FACT_CATEGORY_COLORS, FACT_CATEGORY_LABELS }
export type {
    AttioIgnoreRule,
    AttioIgnoreRuleType,
    CrmCompany,
    CrmContact,
    CrmFact,
    CrmProcessJob,
    CrmProcessJobSourceProgress,
    CrmRecallResult,
    CrmRunResult,
    CrmTeamMember,
    FileSearchDocContent,
    FileSearchDocInfo,
    FileSearchQueryResult,
    FileSearchStoreInfo,
}

export async function listCompanies(params?: { status?: 'needs_review'; limit?: number; offset?: number }): Promise<CrmCompany[]> {
    const { data } = await oracleServiceAxios.get<{ success: boolean; data: CrmCompany[] }>('/companies', { params })
    return data.data
}

export async function getCompanyFacts(companyId: string, limit?: number): Promise<CrmFact[]> {
    const { data } = await oracleServiceAxios.get<{ success: boolean; data: CrmFact[] }>(`/companies/${companyId}/facts`, {
        params: limit ? { limit } : undefined,
    })
    return data.data
}

export async function getCompanyContacts(companyId: string): Promise<CrmContact[]> {
    const { data } = await oracleServiceAxios.get<{ success: boolean; data: CrmContact[] }>(`/companies/${companyId}/contacts`)
    return data.data
}

export async function mergeCompanies(fromId: string, intoId: string): Promise<void> {
    await oracleServiceAxios.post(`/companies/${fromId}/merge`, { into_id: intoId })
}

export async function resolveCompanyReview(companyId: string): Promise<void> {
    await oracleServiceAxios.post(`/companies/${companyId}/resolve-review`)
}

export async function deleteCompany(companyId: string): Promise<void> {
    await oracleServiceAxios.delete(`/companies/${companyId}`)
}

export async function askCrmAgent(query: string): Promise<CrmRecallResult> {
    const { data } = await oracleServiceAxios.post<{ success: boolean; data: CrmRecallResult }>('/ask', { q: query })
    return data.data
}

export async function triggerProcess(opts?: {
    endDate?: string
    source?: 'telegram' | 'email' | 'calendar' | 'manual' | 'attio' | 'telegram-dedicated' | 'ringcentral'
}): Promise<{ jobId: string }> {
    const { data } = await oracleServiceAxios.post<{ success: boolean; data: { jobId: string } }>('/process', opts ?? {})
    return data.data
}

// ─── Attio data source ────────────────────────────────────────────────────────

// Internal lifecycle for an Attio row in ds-attio-messages.
// not_synced is frontend-only (no row exists). The rest are Firestore values.
export type AttioMessageStatus = 'ready' | 'unattributed' | 'failed' | 'in_crm' | 'jibberish'

export interface AttioStatus {
    cursors: { meetings_cursor: string | null }
    counts: { ready: number; in_crm: number; failed: number; unattributed: number; jibberish: number }
}

export interface AttioStructuredContact {
    sourceId: string
    email?: string
    phone?: string
    name?: string
    title?: string
}

export interface AttioMessage {
    entry_id: string
    entry_type: 'call_transcript' | 'note'
    status: AttioMessageStatus
    attio_created_at: string
    external_attribution: { system: string; externalId: string; nameHint: string; domainHint?: string; domains?: string[] } | null
    structured_contacts: AttioStructuredContact[]
    formatted_text: string
    created_at: string | null
    processed_at: string | null
}

export async function getAttioStatus(): Promise<AttioStatus> {
    const { data } = await oracleServiceAxios.get<{ success: boolean; data: AttioStatus }>('/sources/attio/status')
    return data.data
}

export async function listAttioMessages(params?: {
    status?: string
    entry_type?: 'call_transcript' | 'note'
    limit?: number
    offset?: number
}): Promise<AttioMessage[]> {
    const { data } = await oracleServiceAxios.get<{ success: boolean; data: AttioMessage[] }>('/sources/attio/messages', { params })
    return data.data
}

export async function retryAttioMessages(): Promise<{ reset: number }> {
    const { data } = await oracleServiceAxios.post<{ success: boolean; data: { reset: number } }>('/sources/attio/retry', {})
    return data.data
}

export async function listAttioIgnoreRules(): Promise<AttioIgnoreRule[]> {
    const { data } = await oracleServiceAxios.get<{ success: boolean; data: AttioIgnoreRule[] }>('/sources/attio/ignore-rules')
    return data.data
}

export async function createAttioIgnoreRule(input: { type: AttioIgnoreRuleType; value: string; label: string }): Promise<AttioIgnoreRule> {
    const { data } = await oracleServiceAxios.post<{ success: boolean; data: AttioIgnoreRule }>('/sources/attio/ignore-rules', input)
    return data.data
}

export async function deleteAttioIgnoreRule(id: string): Promise<void> {
    await oracleServiceAxios.delete(`/sources/attio/ignore-rules/${encodeURIComponent(id)}`)
}

export async function prepAttioCalls(params: { from: string; to: string; force?: boolean }): Promise<{ prepped: number; jibberish: number; skipped: number }> {
    const { data } = await oracleServiceAxios.post<{ success: boolean; data: { prepped: number; jibberish: number; skipped: number } }>(
        '/sources/attio/calls/prep-range',
        params
    )
    return data.data
}

export async function previewAttioBatch(limit = 25): Promise<SourceBatch[]> {
    const { data } = await oracleServiceAxios.post<{ success: boolean; data: SourceBatch[] }>('/sources/attio/preview', null, { params: { limit } })
    return data.data
}

export interface PipelineConfig {
    activeSources: string[]
    schedule: string
    batchLimit: number
    sourceConfigs: Record<string, Record<string, unknown>>
}

export async function getPipelineConfig(): Promise<PipelineConfig> {
    const { data } = await oracleServiceAxios.get<{ success: boolean; data: PipelineConfig }>('/pipeline-config')
    return data.data
}

export async function setPipelineConfig(patch: Partial<PipelineConfig>): Promise<PipelineConfig> {
    const { data } = await oracleServiceAxios.put<{ success: boolean; data: PipelineConfig }>('/pipeline-config', patch)
    return data.data
}

// ─── Telegram Dedicated ────────────────────────────────────────────────────────
// Telegram account + group management now lives in `oracleTelegramApi.ts` (see
// `/v1/telegram-dedicated/accounts/...` + `/v1/telegram-dedicated/groups/...`).
// What remains here is the CRM-corpus-level surface: messages browser, stats,
// dry-run preview, requeue. Those operate on the entire crm_telegram_messages
// collection and do not need single-channel config.

export type TelegramMediaType = 'photo' | 'voice' | 'audio' | 'video' | 'video_note' | 'document'
export type TelegramMediaStatus = 'none' | 'pending' | 'processing' | 'transcribed' | 'failed'

export interface TelegramDedicatedStats {
    counts: {
        pending: number
        processed: number
        unattributed: number
        failed: number
        total: number
    }
    mediaAwaitingTranscription: number
    lastIngestedAt: string | null
    firstIngestedAt: string | null
    weeklyVolume: { date: string; count: number }[]
    topContributors: { from_id: string; from_username: string; count: number }[]
    cursor: {
        lastFetchAt: string
        lastFetchOldestMsgAt: string | null
        lastFetchCount: number
    } | null
}

export interface TelegramMediaExtraction {
    transcript: string
    doc_type: string | null
    companies: string[]
    people: string[]
    locations: string[]
    references: string[]
    dates: string[]
    visible_text: string | null
}

export interface TelegramDedicatedRecentMessage {
    id: string
    from_username: string
    from_id: string
    text: string
    caption: string | null
    status: 'pending' | 'processed' | 'unattributed' | 'failed'
    media_type: TelegramMediaType | null
    media_status: TelegramMediaStatus | null
    file_name: string | null
    audio_title: string | null
    audio_performer: string | null
    transcribed_text: string | null
    extracted_entities: TelegramMediaExtraction | null
    created_at: string
    processed_at: string | null
    reply_to_id: string | null
    forward_from: string | null
}

export async function getTelegramDedicatedStats(): Promise<TelegramDedicatedStats | null> {
    const { data } = await oracleServiceAxios.get<{ success: boolean; data: TelegramDedicatedStats | null }>('/telegram-dedicated/stats')
    return data.data
}

// ─── Channel-first redesign ─────────────────────────────────────────────────
export type TelegramChannelStatus = 'active' | 'stale' | 'error' | 'paused' | 'unassigned'
export interface TelegramChannelHealth {
    peerId: string
    chatId: string
    name: string | null
    brokerCompanyName: string | null
    watchingPhones: string[]
    anyAccountRunning: boolean
    lastIngestedAt: string | null
    lastProcessedAt: string | null
    oldestPendingAt: string | null
    lagSec: number | null
    counts: { pending: number; processed: number; unattributed: number; failed: number }
    media: { pending: number; processing: number; transcribed: number; failed: number }
    status: TelegramChannelStatus
}

export async function getTelegramDedicatedChannels(): Promise<TelegramChannelHealth[]> {
    const { data } = await oracleServiceAxios.get<{ success: boolean; data: TelegramChannelHealth[] }>('/telegram-dedicated/channels')
    return data.data
}

export async function getTelegramCustomerChannels(): Promise<TelegramChannelHealth[]> {
    const { data } = await oracleServiceAxios.get<{ success: boolean; data: TelegramChannelHealth[] }>('/telegram-customer/channels')
    return data.data
}

interface DeleteTelegramChannelResult {
    messages: number
    watches: number
    processed: number
    groupRowDeleted: boolean
    refreshedPhones: string[]
}

export async function deleteTelegramDedicatedChannel(peerId: string): Promise<DeleteTelegramChannelResult> {
    const { data } = await oracleServiceAxios.delete<{ success: boolean; data: DeleteTelegramChannelResult }>(
        `/telegram-dedicated/channels/${encodeURIComponent(peerId)}`
    )
    return data.data
}

export async function deleteTelegramCustomerChannel(peerId: string): Promise<DeleteTelegramChannelResult> {
    const { data } = await oracleServiceAxios.delete<{ success: boolean; data: DeleteTelegramChannelResult }>(
        `/telegram-customer/channels/${encodeURIComponent(peerId)}`
    )
    return data.data
}

export async function deleteTelegramDedicatedMessage(id: string): Promise<void> {
    await oracleServiceAxios.delete(`/telegram-dedicated/messages/${encodeURIComponent(id)}`)
}

// ─── DataSource SourceBatch contract (mirrors crm-service/src/sources/types.ts) ─
interface SourceHintContact {
    sourceId: string
    name?: string
    email?: string
    phone?: string
    title?: string
}
interface SourceHintAttribution {
    sourceId: string
    system: string
    externalId: string
    nameHint?: string
    domains?: string[]
}
export interface SourceHints {
    contacts?: SourceHintContact[]
    attributions?: SourceHintAttribution[]
}
export interface SourceBatch {
    sourceType: string
    sourceIds: string[]
    text: string
    hints?: SourceHints
}

export interface ListTelegramDedicatedMessagesParams {
    chat_id?: string
    status?: 'pending' | 'processed' | 'unattributed' | 'failed'
    media_status?: TelegramMediaStatus
    cursor?: string | null
    limit?: number
    direction?: 'older' | 'newer'
}

interface ListTelegramDedicatedMessagesResult {
    items: TelegramDedicatedRecentMessage[]
    nextCursor: string | null
    prevCursor: string | null
}

export async function listTelegramDedicatedMessages(params: ListTelegramDedicatedMessagesParams): Promise<ListTelegramDedicatedMessagesResult> {
    const query: Record<string, string | number> = {}
    if (params.chat_id) query['chat_id'] = params.chat_id
    if (params.status) query['status'] = params.status
    if (params.media_status) query['media_status'] = params.media_status
    if (params.cursor) query['cursor'] = params.cursor
    if (params.limit != null) query['limit'] = params.limit
    if (params.direction) query['direction'] = params.direction
    const { data } = await oracleServiceAxios.get<{ success: boolean; data: ListTelegramDedicatedMessagesResult }>('/telegram-dedicated/messages', {
        params: query,
    })
    return data.data
}

export async function requeueTelegramDedicatedMessage(id: string): Promise<void> {
    await oracleServiceAxios.put(`/telegram-dedicated/messages/${encodeURIComponent(id)}/requeue`)
}

export async function previewTelegramDedicatedBatch(limit = 25): Promise<SourceBatch[]> {
    const { data } = await oracleServiceAxios.post<{ success: boolean; data: SourceBatch[] }>('/telegram-dedicated/preview', null, {
        params: { limit },
    })
    return data.data
}

// ─── Telegram Customer Channels messages ─────────────────────────────────────

export async function listTelegramCustomerMessages(params: ListTelegramDedicatedMessagesParams): Promise<ListTelegramDedicatedMessagesResult> {
    const query: Record<string, string | number> = {}
    if (params.chat_id) query['chat_id'] = params.chat_id
    if (params.status) query['status'] = params.status
    if (params.cursor) query['cursor'] = params.cursor
    if (params.limit != null) query['limit'] = params.limit
    if (params.direction) query['direction'] = params.direction
    const { data } = await oracleServiceAxios.get<{ success: boolean; data: ListTelegramDedicatedMessagesResult }>('/telegram-customer/messages', {
        params: query,
    })
    return data.data
}

export async function deleteTelegramCustomerMessage(id: string): Promise<void> {
    await oracleServiceAxios.delete(`/telegram-customer/messages/${encodeURIComponent(id)}`)
}

export async function requeueTelegramCustomerMessage(id: string): Promise<void> {
    await oracleServiceAxios.put(`/telegram-customer/messages/${encodeURIComponent(id)}/requeue`)
}

export async function cancelProcess(jobId: string): Promise<{ status: string }> {
    const { data } = await oracleServiceAxios.post<{ success: boolean; data: { status: string } }>(`/process/cancel/${jobId}`, {})
    return data.data
}

export async function getProcessJobStatus(jobId: string): Promise<CrmProcessJob> {
    const { data } = await oracleServiceAxios.get<{ success: boolean; data: CrmProcessJob }>(`/process/status/${jobId}`)
    return data.data
}

export async function getLatestProcessJob(): Promise<CrmProcessJob | null> {
    const { data } = await oracleServiceAxios.get<{ success: boolean; data: CrmProcessJob | null }>('/process/status/latest')
    return data.data
}

// ─── Attio calls (direct Attio API proxy) ────────────────────────────────────

export interface AttioCallParticipant {
    email_address: string
    status: string
    is_organizer: boolean
}

export interface AttioLinkedRecord {
    object_slug: string
    object_id: string
    record_id: string
    name: string | null
}

export interface AttioCallItem {
    id: { workspace_id: string; meeting_id: string }
    title: string | null
    start: { datetime: string; timezone: string | null } | { date: string }
    end: { datetime: string; timezone: string | null } | { date: string } | null
    participants: AttioCallParticipant[]
    linked_records: AttioLinkedRecord[]
}

export interface AttioCallRecording {
    id: { workspace_id: string; meeting_id: string; call_recording_id: string }
    status: 'processing' | 'completed' | 'failed'
    created_at: string
}

export interface AttioTranscriptSegment {
    speech: string
    start_time: number
    end_time: number
    speaker: { name: string }
}

interface ListAttioCallsResult {
    items: AttioCallItem[]
    nextCursor: string | null
}

export async function listAttioCalls(params?: { ends_from?: string; starts_before?: string; cursor?: string }): Promise<ListAttioCallsResult> {
    const { data } = await oracleServiceAxios.get<{ success: boolean; data: ListAttioCallsResult }>('/sources/attio/calls', { params })
    return data.data
}

export async function getAttioCallRecordings(meetingId: string): Promise<AttioCallRecording[]> {
    const { data } = await oracleServiceAxios.get<{ success: boolean; data: AttioCallRecording[] }>(`/sources/attio/calls/${meetingId}/recordings`)
    return data.data
}

export async function getAttioCallTranscript(meetingId: string, recordingId: string): Promise<AttioTranscriptSegment[]> {
    const { data } = await oracleServiceAxios.get<{ success: boolean; data: AttioTranscriptSegment[] }>(
        `/sources/attio/calls/${meetingId}/recordings/${recordingId}/transcript`
    )
    return data.data
}

interface AttioAdHocSyncResult {
    entryIds: string[]
    skipped: string[]
}

export async function syncAttioCall(meetingId: string, force?: boolean): Promise<AttioAdHocSyncResult> {
    const { data } = await oracleServiceAxios.post<{ success: boolean; data: AttioAdHocSyncResult }>(
        `/sources/attio/calls/${meetingId}/sync`,
        null,
        force ? { params: { force: 'true' } } : undefined
    )
    return data.data
}

// ─── Team Members ─────────────────────────────────────────────────────────────

export interface TeamMemberInput {
    name: string
    telegram_handles?: string[]
    emails?: string[]
    phone_numbers?: string[]
}

export async function listTeamMembers(): Promise<CrmTeamMember[]> {
    const { data } = await oracleServiceAxios.get<{ success: boolean; data: CrmTeamMember[] }>('/team-members')
    return data.data
}

export async function createTeamMember(input: TeamMemberInput): Promise<CrmTeamMember> {
    const { data } = await oracleServiceAxios.post<{ success: boolean; data: CrmTeamMember }>('/team-members', input)
    return data.data
}

export async function updateTeamMember(id: string, input: Partial<TeamMemberInput>): Promise<CrmTeamMember> {
    const { data } = await oracleServiceAxios.put<{ success: boolean; data: CrmTeamMember }>(`/team-members/${id}`, input)
    return data.data
}

export async function deleteTeamMember(id: string): Promise<void> {
    await oracleServiceAxios.delete(`/team-members/${id}`)
}

export async function resetAttioData(): Promise<{ counts: Record<string, number> }> {
    const { data } = await oracleServiceAxios.post<{ success: boolean; data: { counts: Record<string, number> } }>('/sources/attio/reset', {})
    return data.data
}

export async function listRunResults(limit?: number): Promise<CrmRunResult[]> {
    const { data } = await oracleServiceAxios.get<{ success: boolean; data: CrmRunResult[] }>('/admin/run-results', {
        params: limit ? { limit } : undefined,
    })
    return data.data
}

// ----- File Search debug surface -----

export async function getFileSearchStore(): Promise<FileSearchStoreInfo> {
    const { data } = await oracleServiceAxios.get<{ success: boolean; data: FileSearchStoreInfo }>('/file-search/store')
    return data.data
}

export async function listFileSearchDocuments(limit?: number): Promise<{ docs: FileSearchDocInfo[]; has_more: boolean; total_returned: number }> {
    const { data } = await oracleServiceAxios.get<{
        success: boolean
        data: { docs: FileSearchDocInfo[]; has_more: boolean; total_returned: number }
    }>('/file-search/documents', { params: limit ? { limit } : undefined })
    return data.data
}

export async function deleteFileSearchDocument(name: string): Promise<void> {
    await oracleServiceAxios.delete(`/file-search/documents/${encodeURIComponent(name)}`)
}

export async function searchFileSearch(query: string, companyId?: string): Promise<FileSearchQueryResult> {
    const { data } = await oracleServiceAxios.post<{ success: boolean; data: FileSearchQueryResult }>('/file-search/search', { query, companyId })
    return data.data
}

export async function getFileSearchDocumentContent(name: string): Promise<FileSearchDocContent> {
    const { data } = await oracleServiceAxios.get<{ success: boolean; data: FileSearchDocContent }>(
        `/file-search/documents/${encodeURIComponent(name)}/content`
    )
    return data.data
}

export async function listFileSearchDocumentsForCompany(companyId: string): Promise<FileSearchByCompanyResult> {
    const { data } = await oracleServiceAxios.get<{ success: boolean; data: FileSearchByCompanyResult }>(
        `/file-search/by-company/${encodeURIComponent(companyId)}`
    )
    return data.data
}

// ── MCP access ──────────────────────────────────────────────────────────────

export interface McpTokenResult {
    token: string
    expiresAt: string
}

// Mint a per-user MCP bearer token. The caller's Gluon session JWT (attached by
// the axios interceptor) is verified server-side; the returned token is scoped to
// the /mcp endpoint and shown to the user once.
export async function mintMcpToken(): Promise<McpTokenResult> {
    const { data } = await oracleServiceAxios.post<{ success: boolean; data: McpTokenResult }>('/docs/auth/token', {})
    return data.data
}

// The public MCP endpoint URL (for Bearer-token clients: Claude Code, Manus).
export function getMcpEndpointUrl(): string {
    const base = (oracleServiceAxios.defaults.baseURL || '').replace(/\/v1\/?$/, '')
    return `${base}/v1/docs/mcp`
}

// Oracle service base URL (no path suffix).
// OAuth clients (Claude.ai web, Perplexity) connect to this URL and auto-discover
// the OAuth endpoints via /.well-known/oauth-protected-resource (RFC 8707).
export function getMcpOAuthBaseUrl(): string {
    return (oracleServiceAxios.defaults.baseURL || '').replace(/\/v1\/?$/, '')
}
